import {
  Color,
  ExtrudeGeometry,
  LineSegments,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Object3DEventMap,
  Scene,
  Shape,
  ShapeGeometry,
  Vector2,
  Vector3,
} from "three";
import { ADDITION, Brush, Evaluator, SUBTRACTION } from "three-bvh-csg";
import { generateUUID } from "three/src/math/MathUtils.js";
import { CameraInterface } from "../../engine/Engine";
import { Line } from "../Line";
import { Meter } from "../Meter";
import { Point } from "../Point";
import { AbstractWall } from "./AbstractWall";
import { Register } from "../../class/ClassRegistry";

const evaluator = new Evaluator();
evaluator.useGroups = true;
evaluator.debug.enabled = true;

@Register("Wall")
export class Wall extends AbstractWall {
  protected _height: number = 2.2;

  protected _edges?: LineSegments;
  protected _edgeColor2d?: Color;
  protected _edgeColor3d?: Color;
  protected _mesh3dMaterial?: MeshStandardMaterial = new MeshStandardMaterial();

  protected _mesh3D: Brush = new Brush();

  constructor(start: Point, end: Point) {
    super(start, end);
    this._type = "Wall";
    this._name = "Wall_" + generateUUID();
    this._root = new Object3D();
    this._root.name = this._name;

    this._isVisible = true;
    this._color2d = new Color(0xaaaaaa);
    this._color3d = new Color(0xcccccc);
    this._root = new Mesh();
    this._root.userData["parent"] = this;
    this.raycastable = false;
  }

  get isConstruct() {
    return true;
  }

  get hidden(): boolean {
    return this._hidden;
  }

  set hidden(value: boolean) {
    if (this._root) this._root.visible = value && this._isVisible;
    this._hidden = value;
  }

  get visible(): boolean {
    return this._isVisible;
  }

  set visible(value: boolean) {
    if (this._root) this._root.visible = value && !this._hidden;
    this._isVisible = value;
  }

  get showEdges(): boolean {
    return this.edges && this.edges?.visible;
  }

  set showEdges(value: boolean) {
    if (this.edges) this.edges.visible = value;
  }

  set visibility(value: boolean) {
    this.visible = value;
  }

  get collider(): Object3D<Object3DEventMap> | undefined {
    this._mesh2D.userData["owner"] = this;
    return this._mesh2D;
  }

  get scaleObb(): Vector3 {
    this._scaleObb.set(this.length, this._height, this._thickness);
    return this._scaleObb;
  }

  resetColor(): void {
    super.color2d = new Color(0xaaaaaa);
  }

  protected initializeMeter(): void {
    super.initializeMeter();
    this._meter?.dispose();
    this._meter = new Meter(this, undefined, undefined, {
      minScale: 0.75,
      maxScale: 0.75,
      offset: new Vector3(0, 0, -0.2),
    });
    this._meter.name = "wall";
  }

  protected initializeColors(): void {
    this._color2d = new Color(0xaaaaaa);
    this._color3d = new Color(0xcccccc);
  }

  set renderMode(value: string) {
    if (this._renderMode != value) this.isDirty = true;
    super.renderMode = value;
  }

  private _createBase3DGeometry() {
    const shape = this.createBaseShape();
    const extrudeSettings = {
      steps: 1,
      depth: this._height,
      bevelEnabled: false,
      bevelThickness: 0.0,
      bevelSize: 0,
      bevelOffset: 0,
      bevelSegments: 0,
    };
    const geometry = new ExtrudeGeometry(shape, extrudeSettings);
    return geometry;
  }

  private _render3D(color: Color) {
    this._mesh3D?.geometry?.dispose();
    this._mesh3D.traverse((o) => {
      if (o instanceof Mesh || o instanceof Brush) o.geometry?.dispose();
    });
    const mesh = new Brush();
    mesh.material = this._mesh3dMaterial!;
    mesh.name = "linemesh3D_of_" + this.name;
    mesh.geometry?.dispose();
    mesh.geometry = this._createBase3DGeometry();
    mesh.rotation.set(-Math.PI * 0.5, 0, 0);

    this._mesh3D = mesh;
    this._root?.add(mesh);
    this._root?.position.copy(this._point!);
    this._root?.quaternion.copy(this.rotation!);
    (mesh.material as MeshStandardMaterial).color = color;

    mesh.updateMatrixWorld(true);
  }

  private mergeChildMesh(color: Color) {
    if (this.childs && this.childs.length < 1) return;

    const tmpCrometry = new ShapeGeometry(
      new Shape([new Vector2(), new Vector2()])
    );

    let tmpBrush = new Brush(tmpCrometry);

    this.childs?.forEach((c) => {
      if (c instanceof Line) {
        c.isDirty = true;
        const brush = c.brush;
        if (brush) {
          brush.material = this._mesh3D.material;
          tmpBrush = evaluator.evaluate(tmpBrush, brush, ADDITION);
        }
      }
    });

    evaluator.evaluate(this._mesh3D, tmpBrush, SUBTRACTION, this._mesh3D);
    this._mesh3D.geometry.applyMatrix4(
      this._mesh3D.matrixWorld.clone().invert()
    );
    this._mesh3D.material = new MeshStandardMaterial({ color });
  }

  render3d(scene?: Scene, camera?: CameraInterface): void {
    scene?.add(this._root!);
    if (this.root) this.root.visible = this.visible && !this.hidden;

    this._meter?.removeFromParent();
    this._menu2D?.removeFromParent();

    this._render3D(this._color3d!);
    this.mergeChildMesh(this._color3d!);

    this._childs?.forEach((c) => {
      c.update();
      c.root?.removeFromParent();
    });
  }

  render(scene?: Scene, camera?: CameraInterface): void {
    this._mesh3D.removeFromParent();
    super.render(scene, camera);
  }

  removeFromParent(): void {
    super.removeFromParent();
    this._root?.removeFromParent();
    this._meter?.root.removeFromParent();
  }
}
